import { computed, defineComponent, ref } from 'vue';
import { storeToRefs } from 'pinia';
import {
  genFileId,
  UploadFile,
  UploadInstance,
  UploadRawFile,
} from 'element-plus';
import { useLimitExceptionsUploadStore } from '@/store/limit-exceptions-upload/limit-exceptions-upload.store';
import {
  ACCEPT_EXCEL_EXTENSIONS,
  EXCEL_EXTENSION_REGEX,
} from '@/models/limit-exceptions-upload/limit-exceptions-upload.model';
import { showErrorNotification } from '@/utils/show-notification.util';

export default defineComponent({
  name: 'UploadLimitExceptionsDialog',
  setup() {
    const uploadRef = ref<UploadInstance | null>(null);

    const limitExceptionsUploadStore = useLimitExceptionsUploadStore();
    const {
      isUploadModalOpen,
      isUploadLoading,
      uploadErrors,
      uploadedFile,
      totalRowsReceived,
      totalRowsProcessedSuccessfully,
    } = storeToRefs(limitExceptionsUploadStore);
    const {
      setUploadedFile,
      clearUploadErrors,
      uploadLimitExceptionsFile,
      closeUploadModal,
    } = limitExceptionsUploadStore;

    const isErrorsPresent = computed<boolean>(() => uploadErrors.value.length > 0);

    const isSuccessAlertVisible = computed<boolean>(
      () => totalRowsProcessedSuccessfully.value > 0,
    );

    const successAlertTitle = computed<string>(
      () => `Successfully processed ${totalRowsProcessedSuccessfully.value} of ${totalRowsReceived.value} rows`,
    );

    const isUploadDisabled = computed<boolean>(
      () => !uploadedFile.value || isUploadLoading.value,
    );

    const isSelectFileDisabled = computed<boolean>(
      () => Boolean(uploadedFile.value),
    );

    const removeUploadedFile = (): void => {
      setUploadedFile(null);
      clearUploadErrors();
      uploadRef.value?.clearFiles();
    };

    const changeUploadedFile = (uploadFile: UploadFile): void => {
      const file = uploadFile.raw;

      if (!file) {
        removeUploadedFile();

        return;
      }

      if (!EXCEL_EXTENSION_REGEX.test(file.name)) {
        showErrorNotification(new Error(`Only Excel files ${ACCEPT_EXCEL_EXTENSIONS} are allowed`));
        removeUploadedFile();

        return;
      }

      setUploadedFile(file);
      clearUploadErrors();
    };

    const replaceUploadedFile = (fileList: File[]): void => {
      uploadRef.value?.clearFiles();

      const rawFile = fileList[0] as UploadRawFile;

      if (rawFile) {
        rawFile.uid = genFileId();
        uploadRef.value?.handleStart(rawFile);
      }
    };

    const closeUploadModalAction = (): void => {
      uploadRef.value?.clearFiles();
      closeUploadModal();
    };

    return {
      ACCEPT_EXCEL_EXTENSIONS,
      uploadRef,
      isUploadModalOpen,
      isUploadLoading,
      uploadErrors,
      uploadedFile,
      isErrorsPresent,
      isSuccessAlertVisible,
      successAlertTitle,
      isUploadDisabled,
      isSelectFileDisabled,
      changeUploadedFile,
      removeUploadedFile,
      replaceUploadedFile,
      uploadLimitExceptionsFile,
      closeUploadModalAction,
    };
  },
});
